import {
  ForwardedRef,
  forwardRef,
  HTMLProps,
  ReactNode,
  RefObject,
  SyntheticEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getDOMProps } from 'app/shared/utils/react';
import styled from 'styled-components';

import { AutocompleteTextField } from 'app/shared/u21-ui/components/input/select/styles';
import { Autocomplete, InputAdornment } from '@mui/material';
import {
  ClearButton,
  Loading,
  StartIconContainer,
} from 'app/shared/u21-ui/components/input/text-field/styles';
import { U21Chip } from 'app/shared/u21-ui/components/display/U21Chip';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import { U21TextFieldProps } from 'app/shared/u21-ui/components/input/text-field/U21TextFieldInputProps';

export interface U21TextListFieldProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'onChange' | 'readOnly' | 'value'
> {
  autoFocus?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  error?: boolean;
  endIcon?: ReactNode;
  inputProps?: U21TextFieldProps['inputProps'];
  label?: string;
  loading?: boolean;
  placeholder?: string;
  ref?: RefObject<HTMLDivElement>;
  required?: boolean;
  responsiveLength?: boolean;
  startIcon?: ReactNode;
  getLimitTagsText?: (count: number) => string;
  limitTags?: number;
  onChange: (value: string[], e?: SyntheticEvent) => void;
  value?: string[];
}

const DEFAULT_VALUE = [];

export const U21TextListField = forwardRef(
  (props: U21TextListFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      autoFocus,
      clearable = false,
      disabled,
      endIcon,
      error,
      getLimitTagsText = (count) => `+${count} more`,
      inputProps = {},
      label,
      limitTags,
      loading = false,
      onChange,
      placeholder,
      required,
      responsiveLength = false,
      startIcon,
      value = DEFAULT_VALUE,
      ...rest
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchValue, setSearchValue] = useState<string>('');

    const valueSet = useMemo(() => new Set(value), [value]);

    return (
      <Autocomplete<string, true, boolean>
        disabled={disabled}
        disableClearable={!clearable}
        fullWidth={!responsiveLength}
        getLimitTagsText={getLimitTagsText}
        inputValue={searchValue}
        limitTags={limitTags}
        loading={loading}
        multiple
        onChange={(e, selected) => {
          onChange(selected, e);
        }}
        onInputChange={(e, newSearchValue) => {
          setSearchValue(newSearchValue);
        }}
        onPaste={(e) => {
          const pastedData = e.clipboardData.getData('text');
          if (!pastedData) {
            return;
          }

          e.preventDefault();
          const segments = pastedData.split(/[,\n]/).filter((i) => i !== '');
          const newValues = segments.filter((i) => !valueSet.has(i));
          onChange([...value, ...newValues], e);
        }}
        open={false}
        options={value}
        ref={ref}
        renderInput={(params) => {
          const {
            inputProps: inputInputProps,
            InputProps,
            size,
            ...restParams
          } = params;

          const startAdornment = [
            Boolean(startIcon) && (
              <StartIconContainer $disabled={disabled} key="icon">
                {startIcon}
              </StartIconContainer>
            ),
            InputProps.startAdornment,
          ].filter(Boolean);

          return (
            <StyledAutocompleteTextField
              {...restParams}
              responsiveLength={responsiveLength}
              autoFocus={autoFocus}
              error={error}
              InputLabelProps={{ required }}
              inputProps={{
                ...inputInputProps,
                ...inputProps,
                responsiveLength: true,
              }}
              InputProps={{
                ...InputProps,
                endAdornment: (
                  <EndInputAdornment position="end">
                    {inputRef.current &&
                      searchValue &&
                      !valueSet.has(searchValue) && (
                        <AddButton
                          onClick={(e) => {
                            setSearchValue('');
                            onChange([...value, searchValue], e);
                          }}
                          size="small"
                          tooltip={
                            <TooltipContainer>
                              Add item
                              <br />
                              <U21Typography
                                color="text.secondary"
                                variant="body2"
                              >
                                Enter
                              </U21Typography>
                            </TooltipContainer>
                          }
                        >
                          Add
                        </AddButton>
                      )}
                    {clearable && !disabled && value.length > 0 && (
                      <ClearButton
                        onClick={(e) => {
                          onChange([], e);
                        }}
                      />
                    )}
                    <Loading loading={loading} />
                    {endIcon}
                  </EndInputAdornment>
                ),
                startAdornment:
                  startAdornment.length > 0 ? (
                    <StartInputAdornment position="start">
                      {startAdornment}
                    </StartInputAdornment>
                  ) : undefined,
              }}
              inputRef={inputRef}
              label={label}
              loading={loading}
              onChange={() => {}}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const { value: newValue } = e.target as HTMLInputElement;
                  if (newValue) {
                    if (!valueSet.has(newValue)) {
                      onChange([...value, newValue], e);
                    }
                    // for some reason, input isn't cleared even though searchValue
                    // is reset but is cleared when using setTimeout
                    setTimeout(() => {
                      setSearchValue('');
                    }, 0);
                  }
                  e.preventDefault();
                }
              }}
              // hide placeholder when value is populated
              placeholder={value.length ? '' : placeholder}
            />
          );
        }}
        renderTags={(values, getTagProps) => {
          return values.map((i, index) => {
            const {
              key: drop,
              onDelete,
              ...chipProps
            } = getTagProps({ index });

            return (
              <U21Chip
                // append random string at the end to avoid conflicting `1` key caused by the more text
                key={`${JSON.stringify(i)}-tag`}
                {...chipProps}
                onDelete={disabled ? undefined : onDelete}
              >
                {i}
              </U21Chip>
            );
          });
        }}
        size="small"
        value={value}
        {...getDOMProps(rest)}
      />
    );
  },
);

const AddButton = styled(U21Button)`
  visibility: hidden;
  margin: -1px 0; // reduce margins so it doesn't cause the input to increase in height
`;

const StyledAutocompleteTextField = styled(AutocompleteTextField)`
  .MuiAutocomplete-input.MuiAutocomplete-input.MuiAutocomplete-input {
    min-width: 100px;
  }

  .MuiInputAdornment-root {
    flex-wrap: wrap;
  }

  .Mui-focused ${AddButton}, &:hover ${AddButton} {
    visibility: visible;
  }
`;

const StartInputAdornment = styled(InputAdornment)`
  max-width: 100%;
`;

const EndInputAdornment = styled(InputAdornment)`
  gap: 2px;
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
